// src/components/FeaturesPage.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicPage, FeatureGridSection, Eyebrow, DecorLines } from './public/PublicLayout';

const FeatureCard = ({ icon, title, description, details }) => (
    <div className="pub-surface pub-line border rounded-xl p-7 shadow-lg shadow-black/20">
        <div className="pub-soft w-14 h-14 rounded-lg flex items-center justify-center mb-6">
            <i className={`${icon} text-2xl`}></i>
        </div>
        <h3 className="pub-text text-xl font-bold mb-3">{title}</h3>
        <p className="pub-muted mb-5 leading-relaxed">{description}</p>
        <ul className="pub-muted text-sm space-y-2">
            {details.map((detail, index) => (
                <li key={index} className="flex items-start">
                    <i className="fas fa-check pub-icon mr-3 mt-1 flex-shrink-0"></i>
                    <span>{detail}</span>
                </li>
            ))}
        </ul>
    </div>
);

const SectionHeading = ({ eyebrow, title, children }) => (
    <div className="text-center mb-12">
        <Eyebrow center>{eyebrow}</Eyebrow>
        <h2 className="mt-5 text-3xl sm:text-5xl font-extrabold tracking-tight text-white">{title}</h2>
        {children && <p className="mt-4 text-lg sm:text-xl text-slate-200 max-w-3xl mx-auto">{children}</p>}
    </div>
);

const FeaturesPage = () => {
    const navigate = useNavigate();

    const coreFeatures = [
        {
            icon: "fas fa-truck-ramp-box",
            title: "Load Management",
            description: "Track active loads with pickup and delivery details, from booking to delivery.",
            details: ["Real-time load tracking", "Digital documentation (eBOL)", "GPS location stamps", "Payment collection details"],
        },
        {
            icon: "fas fa-mobile-screen",
            title: "Driver Mobile App",
            description: "An app for drivers. Simple to use, and it works offline.",
            details: ["Upload delivery photos (PODs)", "Capture receiver e-signatures", "Download BOL PDFs", "Weekly earnings tracking"],
        },
        {
            icon: "fas fa-desktop",
            title: "Dispatcher Web Portal",
            description: "A web app for dispatchers to manage the fleet and assign loads from anywhere.",
            details: ["Fleet management dashboard", "Drag-and-drop load assignment", "Driver performance analytics", "Real-time communication"],
        }
    ];

    return (
        <PublicPage>
            <FeatureGridSection headingLevel="h1" />

            {/* Core Features */}
            <section className="pub-page py-16 sm:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionHeading eyebrow="Core tools" title="The tools you use every day">
                        Loads, the driver app and the dispatcher portal.
                    </SectionHeading>

                    <div className="grid md:grid-cols-3 gap-5">
                        {coreFeatures.map(feature => <FeatureCard key={feature.title} {...feature} />)}
                    </div>
                </div>
            </section>

            {/* LiveLoad */}
            <section id="liveload" className="scroll-mt-24 relative overflow-hidden pub-band border-y border-white/10 py-16 sm:py-20">
                <DecorLines />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-start">
                        {/* Left - Content */}
                        <div>
                            <Eyebrow>LiveLoad for dealers</Eyebrow>

                            <h2 className="mt-6 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05] text-white">
                                Same-day vehicle transport
                                <span className="block pub-accent">for dealers</span>
                            </h2>

                            <p className="mt-6 text-lg sm:text-xl text-slate-200">
                                LiveLoad connects car dealers directly with verified carriers for urgent vehicle transport. No brokers. You see every price.
                            </p>

                            <div className="mt-10 space-y-6">
                                <h3 className="text-xl font-bold text-white">How it works</h3>

                                {[
                                    { title: 'Upload your gate pass', text: 'Upload a Manheim, Adesa, OpenLane or other auction gate pass. We read the vehicle details, VIN and pickup location from it.' },
                                    { title: 'Get bids', text: 'Verified carriers in your area see your load and bid on it. You see each carrier\'s rating, insurance status and estimated pickup time.' },
                                    { title: 'Accept and track', text: 'Accept a bid, track pickup and delivery, and pay on delivery. Then rate the carrier.' },
                                ].map((step, i) => (
                                    <div key={step.title} className="flex items-start gap-4">
                                        <span className="w-11 h-11 rounded-full border-2 pub-accent-border flex items-center justify-center flex-shrink-0 font-semibold pub-accent">
                                            {i + 1}
                                        </span>
                                        <div>
                                            <h4 className="text-white font-bold">{step.title}</h4>
                                            <p className="text-slate-300 text-sm leading-relaxed">{step.text}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                <button
                                    onClick={() => navigate('/dealer-register')}
                                    className="px-8 py-3.5 pub-btn rounded-lg font-semibold text-lg transition"
                                >
                                    <i className="fas fa-car mr-3"></i>Register as a dealer
                                </button>
                                <button
                                    onClick={() => navigate('/register')}
                                    className="px-8 py-3.5 border-2 pub-accent-border text-white rounded-lg font-semibold text-lg pub-hover-tint transition"
                                >
                                    <i className="fas fa-truck mr-3"></i>Join as a carrier
                                </button>
                            </div>
                        </div>

                        {/* Right - Features List */}
                        <div className="space-y-3">
                            <h3 className="text-xl font-bold text-white mb-5">What you get</h3>

                            {[
                                { icon: 'fas fa-file-lines', title: 'Gate pass reading', desc: 'Reads Manheim, Adesa, OpenLane, SmartAuction, BacklotCars, Copart and IAAI documents.' },
                                { icon: 'fas fa-clock', title: 'Same-day loads', desc: 'LiveLoads expire at the end of the day, so carriers pick up fast and price to win.' },
                                { icon: 'fas fa-shield-halved', title: 'Insurance verification', desc: 'Every carrier must verify insurance before bidding.' },
                                { icon: 'fas fa-location-dot', title: 'Nearby carriers', desc: 'Carriers see loads within their radius, so pickup is faster.' },
                                { icon: 'fas fa-credit-card', title: 'Secure payments', desc: 'Payment is authorized up front and captured on delivery. 5% platform fee, no hidden costs.' },
                                { icon: 'fas fa-star', title: 'Ratings both ways', desc: 'Dealers and carriers rate each other.' },
                            ].map((feature) => (
                                <div key={feature.title} className="pub-surface pub-line border flex items-start gap-4 rounded-lg p-4">
                                    <div className="pub-soft w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <i className={feature.icon}></i>
                                    </div>
                                    <div>
                                        <h4 className="pub-text font-bold">{feature.title}</h4>
                                        <p className="pub-muted text-sm">{feature.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Supported Auctions */}
                    <div className="mt-12 pt-8 border-t border-white/15">
                        <p className="text-center text-slate-300 mb-5">Supported auction documents</p>
                        <div className="flex flex-wrap justify-center gap-3">
                            {['Manheim', 'Adesa', 'OpenLane', 'SmartAuction', 'BacklotCars', 'Copart', 'IAAI'].map(auction => (
                                <span key={auction} className="pub-soft px-4 py-2 rounded-full text-sm">
                                    {auction}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* More tools */}
            <section className="pub-page py-16 sm:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionHeading eyebrow="More tools" title="Compliance, freight and billing">
                        Tools for safety, different freight types, invoicing and connecting your own systems.
                    </SectionHeading>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {[
                            { icon: 'fas fa-shield-halved', title: 'Safety & Compliance', description: 'Keep DOT compliance with driver qualification files, HOS tracking and vehicle inspection reports (DVIR).' },
                            { icon: 'fas fa-right-left', title: 'Multiple Load Types', description: 'Run auto hauling, dry van, reefer and other freight, with fields for each type.' },
                            { icon: 'fas fa-file-invoice-dollar', title: 'Invoicing', description: 'Create invoices and driver statements with earnings, fuel deductions and expenses.' },
                            { icon: 'fas fa-plug', title: 'API & Webhooks', description: 'Connect LoadMemo to your systems with our API and get updates through webhooks.' }
                        ].map(item => (
                            <div key={item.title} className="pub-surface pub-line border rounded-xl p-6 shadow-lg shadow-black/20">
                                <i className={`${item.icon} pub-icon text-3xl mb-4`}></i>
                                <h3 className="pub-text text-lg font-bold mb-2">{item.title}</h3>
                                <p className="pub-muted text-sm leading-relaxed">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Integrations */}
            <section className="pub-page pb-16 sm:pb-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionHeading eyebrow="Integrations" title="Works with the tools you use">
                        Connect the accounting, fuel and factoring tools you already use.
                    </SectionHeading>

                    <div className="grid md:grid-cols-3 gap-5">
                        {[
                            { icon: 'fas fa-book', title: 'QuickBooks', description: 'Sync invoices, expenses and payments with QuickBooks Online.' },
                            { icon: 'fas fa-gas-pump', title: 'Fuel Cards', description: 'Connect EFS, Fleet One and other fuel cards to import transactions and track IFTA.' },
                            { icon: 'fas fa-credit-card', title: 'Factoring Services', description: 'Send invoices to RTS, OTR and other factoring partners in one click.' }
                        ].map(item => (
                            <div key={item.title} className="pub-surface pub-line border text-center rounded-xl p-6 shadow-lg shadow-black/20">
                                <div className="pub-soft w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5">
                                    <i className={`${item.icon} text-2xl`}></i>
                                </div>
                                <h3 className="pub-text text-xl font-bold mb-2">{item.title}</h3>
                                <p className="pub-muted text-sm leading-relaxed">{item.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative overflow-hidden pub-band border-t border-white/10 py-16 sm:py-20">
                <DecorLines />
                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">See it with your own fleet</h2>
                    <p className="mt-4 text-lg sm:text-xl text-slate-200">
                        Replace spreadsheets and old software with one system for your fleet.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={() => navigate('/contact')}
                            className="px-10 py-3.5 pub-btn rounded-lg font-semibold text-lg transition"
                        >
                            <i className="fas fa-calendar-days mr-3"></i>Request a demo
                        </button>
                        <button
                            onClick={() => navigate('/solutions')}
                            className="px-10 py-3.5 border-2 pub-accent-border text-white rounded-lg font-semibold text-lg pub-hover-tint transition"
                        >
                            <i className="fas fa-truck-moving mr-3"></i>View fleet plans
                        </button>
                    </div>
                </div>
            </section>
        </PublicPage>
    );
};

export default FeaturesPage;
